import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import { login as loginService, logout as logoutService, refreshToken as refreshTokenService } from '../services/auth';
import { useTokenRefresh } from '../hooks/useTokenRefresh';

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  requirePasswordChange?: boolean;
  [key: string]: any;
}

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  requirePasswordChange: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateRequirePasswordChange: (value: boolean) => void;
  refreshToken: (silent?: boolean) => Promise<TokenResponse | null>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  requirePasswordChange: false,
  isLoading: true,
  login: async () => {},
  logout: () => {},
  updateRequirePasswordChange: () => {},
  refreshToken: async () => null,
});

// Listen for logout events from other tabs/windows
const handleLogoutEvent = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  window.location.href = '/login';
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [requirePasswordChange, setRequirePasswordChange] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // The single proactive refresh timer for the whole app lives in this hook.
  // It reschedules itself on 'token:updated' events fired by services/auth.
  useTokenRefresh();

  const refreshToken = useCallback(async (silent = false): Promise<TokenResponse | null> => {
    const currentRefreshToken = localStorage.getItem('refreshToken');
    if (!currentRefreshToken) {
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('token');
      const error = new Error('No refresh token available');
      if (!silent) throw error;
      return null;
    }

    try {
      const result = await refreshTokenService();

      if (!result || !result.user) {
        // Adopted tokens from a parallel refresh carry no user payload —
        // the current session state stays valid, just report the tokens.
        if (result) {
          return {
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
            user: user as User
          };
        }
        const error = new Error('Token refresh failed');
        if (!silent) throw error;
        return null;
      }

      const { accessToken, refreshToken: newRefreshToken, user: refreshedUser } = result;
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      setUser(refreshedUser);
      setIsAuthenticated(true);
      setRequirePasswordChange(!!refreshedUser.requirePasswordChange);

      return {
        accessToken,
        refreshToken: newRefreshToken,
        user: refreshedUser
      };
    } catch (error: any) {

      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');

      if (!location.pathname.includes('/login')) {
        navigate('/login', {
          replace: true,
          state: { from: location.pathname }
        });
      }
      throw error;
    }
  }, [location, navigate, user]);

  const logout = useCallback(() => {
    // Revoke the server-side session (best-effort, never blocks local logout)
    logoutService();
    // Clear auth state
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setRequirePasswordChange(false);
    navigate('/login');
  }, [navigate]);

  const login = async (email: string, password: string) => {
    try {
      const data = await loginService(email, password);
      const { accessToken, refreshToken: newRefreshToken, user: userData } = data;

      if (!accessToken || !newRefreshToken || !userData) {
        throw new Error('Invalid response from server');
      }

      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      setUser(userData);
      setIsAuthenticated(true);
      setRequirePasswordChange(userData.requirePasswordChange || false);

      // Redirect based on role and password change requirement
      if (userData.requirePasswordChange) {
        navigate('/change-password');
      } else {
        const intendedPath = location.state?.from || '/utilities/dashboard';
        navigate(intendedPath, { replace: true });
      }
    } catch (error: any) {

      const errorMessage = error.response?.data?.message || 'Failed to login';
      throw new Error(errorMessage);
    }
  };

  const updateRequirePasswordChange = (value: boolean) => {
    setRequirePasswordChange(value);
  };

  useEffect(() => {
    // Add logout event listener
    window.addEventListener('auth:logout', handleLogoutEvent);

    return () => {
      window.removeEventListener('auth:logout', handleLogoutEvent);
    };
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const currentRefreshToken = localStorage.getItem('refreshToken');

        if (!token || !currentRefreshToken) {
          setIsLoading(false);
          return;
        }

        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        try {
          // Get fresh user data instead of relying on token decode
          const userResponse = await api.get('/auth/me');
          const user = userResponse.data;

          setUser(user);
          setIsAuthenticated(true);
          setRequirePasswordChange(!!user.requirePasswordChange);
          // useTokenRefresh schedules itself off the stored token — nothing
          // else needed here.
        } catch (error) {
          // Stored access token rejected — attempt one refresh
          const refreshResult = await refreshToken();
          if (!refreshResult) {
            throw new Error('Failed to refresh token');
          }
        }
      } catch (error) {

      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        requirePasswordChange,
        isLoading,
        login,
        logout,
        updateRequirePasswordChange,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
